import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { LEISTUNGEN, LEISTUNG_MAP } from '../abrechnung/data/leistungskatalog';
import {
  addZeitEintrag, loadZeiteintraege, toggleAbgerechnet, deleteZeitEintrag,
  updateZeitEintrag,
  type ZeitEintragDB,
} from '../../services/zeitService';

// ── Wochenübersicht ───────────────────────────────────────────────────────

function Wochenuebersicht({ eintraege }: { eintraege: ZeitEintragDB[] }): React.JSX.Element {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);

  const wochentage = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  const tagesMin = wochentage.map(datum => ({
    datum,
    label: new Date(datum + 'T00:00:00').toLocaleDateString('de-AT', { weekday: 'short' }),
    min: eintraege.filter(e => e.datum === datum).reduce((s, e) => s + e.dauerMin, 0),
  }));

  const maxMin   = Math.max(...tagesMin.map(t => t.min), 1);
  const wocheMin = tagesMin.reduce((s, t) => s + t.min, 0);

  const mandantMap = new Map<string, number>();
  for (const e of eintraege) {
    if (wochentage.includes(e.datum)) {
      mandantMap.set(e.mandantId, (mandantMap.get(e.mandantId) ?? 0) + e.dauerMin);
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Diese Woche</h3>
        <span className="text-sm font-bold text-blue-700 dark:text-blue-400">
          {(wocheMin / 60).toFixed(1)} h
        </span>
      </div>

      {/* Tages-Balken */}
      <div className="flex items-end gap-1.5 h-16">
        {tagesMin.map(t => {
          const pct = maxMin > 0 ? (t.min / maxMin) * 100 : 0;
          const isToday = t.datum === now.toISOString().slice(0, 10);
          return (
            <div key={t.datum} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col justify-end" style={{ height: '48px' }}>
                <div
                  className={`w-full rounded-t transition-all ${isToday ? 'bg-blue-600' : 'bg-blue-200 dark:bg-blue-800'}`}
                  style={{ height: `${Math.max(pct, t.min > 0 ? 8 : 0)}%` }}
                  title={`${t.label}: ${(t.min / 60).toFixed(1)} h`}
                />
              </div>
              <span className={`text-xs ${isToday ? 'font-bold text-blue-700 dark:text-blue-400' : 'text-gray-400'}`}>
                {t.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Mandant-Split */}
      {mandantMap.size > 0 && (
        <div className="space-y-1.5 pt-1 border-t border-gray-100 dark:border-gray-800">
          {Array.from(mandantMap.entries()).map(([id, min]) => (
            <div key={id} className="flex items-center justify-between text-xs">
              <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-600 dark:text-gray-400">{id || '—'}</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">{(min / 60).toFixed(1)} h</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stoppuhr ──────────────────────────────────────────────────────────────

function Stoppuhr({
  onStop,
}: {
  onStop: (dauerMin: number, startISO: string, endeISO: string) => void;
}): React.JSX.Element {
  const [running, setRunning]   = useState(false);
  const [elapsed, setElapsed]   = useState(0);
  const [startISO, setStartISO] = useState('');
  const intervalRef = useRef<number | null>(null);

  const start = (): void => {
    const now = new Date();
    setStartISO(now.toISOString());
    setRunning(true);
    setElapsed(0);
    intervalRef.current = window.setInterval(() => {
      setElapsed(s => s + 1);
    }, 1000);
  };

  const stop = (): void => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);
    const dauerMin = Math.max(1, Math.ceil(elapsed / 60));
    const endeISO  = new Date().toISOString();
    onStop(dauerMin, startISO, endeISO);
    setElapsed(0);
  };

  useEffect(() => () => {
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
  }, []);

  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const display = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;

  return (
    <div className="card text-center space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Stoppuhr</h2>
      <div className={`text-5xl font-mono font-bold tabular-nums transition-colors
        ${running ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400'}`}>
        {display}
      </div>
      {!running ? (
        <button onClick={start} className="btn-primary mx-auto">▶ Starten</button>
      ) : (
        <button onClick={stop}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white
                     font-medium text-sm hover:bg-red-700 transition-colors mx-auto">
          ■ Stopp & Speichern
        </button>
      )}
    </div>
  );
}

// ── Manuelle Eingabe ──────────────────────────────────────────────────────

function ManuelleEingabe({ onAdd }: { onAdd: (e: Omit<ZeitEintragDB, 'id' | 'erstelltAm'>) => void }): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [datum,        setDatum]        = useState(new Date().toISOString().slice(0, 10));
  const [start,        setStart]        = useState('08:00');
  const [ende,         setEnde]         = useState('09:00');
  const [leistung,     setLeistung]     = useState('BER-01');
  const [beschreibung, setBeschreibung] = useState('');

  const dauerMin = useMemo(() => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = ende.split(':').map(Number);
    return Math.max(0, ((eh ?? 0) * 60 + (em ?? 0)) - ((sh ?? 0) * 60 + (sm ?? 0)));
  }, [start, ende]);

  const handleAdd = (): void => {
    if (dauerMin <= 0) return;
    onAdd({
      datum,
      startISO:    `${datum}T${start}:00`,
      endeISO:     `${datum}T${ende}:00`,
      dauerMin,
      mandantId:   mandant?.id ?? '',
      leistungCode: leistung,
      beschreibung,
      abgerechnet: false,
    });
    setBeschreibung('');
  };

  return (
    <div className="card space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Manuelle Eingabe</h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1 col-span-2">
          <label className="label-text">Datum</label>
          <input type="date" value={datum} onChange={e => { setDatum(e.target.value); }} className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Start</label>
          <input type="time" value={start} onChange={e => { setStart(e.target.value); }} className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Ende</label>
          <input type="time" value={ende} onChange={e => { setEnde(e.target.value); }} className="input-field" />
        </div>
      </div>
      {dauerMin > 0 ? (
        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
          ⏱ {Math.floor(dauerMin / 60) > 0 ? `${Math.floor(dauerMin / 60)} Std. ` : ''}
          {dauerMin % 60 > 0 ? `${dauerMin % 60} Min.` : ''} ({(dauerMin / 60).toFixed(2).replace('.', ',')} h)
        </p>
      ) : (
        <p className="text-xs text-red-500 font-medium">⚠ Ende muss nach Start liegen</p>
      )}
      <div className="space-y-1">
        <label className="label-text">Leistungsart</label>
        <select value={leistung} onChange={e => { setLeistung(e.target.value); }} className="input-field">
          {LEISTUNGEN.filter(l => l.einheit === 'Std').map(l => (
            <option key={l.code} value={l.code}>{l.code} — {l.bezeichnung}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="label-text">Beschreibung</label>
        <input value={beschreibung} onChange={e => { setBeschreibung(e.target.value); }}
          placeholder="z.B. Betriebsbegehung Produktion" className="input-field" />
      </div>
      <button onClick={handleAdd} disabled={dauerMin <= 0}
        className="btn-primary w-full justify-center disabled:opacity-50">
        + Eintrag hinzufügen
      </button>
    </div>
  );
}

// ── Eintrag bearbeiten (inline) ───────────────────────────────────────────

function EintragEditForm({ eintrag, onSave, onCancel }: {
  eintrag:  ZeitEintragDB;
  onSave:   (e: ZeitEintragDB) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const mandanten = useMandantStore(s => s.mandanten);
  const [datum,        setDatum]        = useState(eintrag.datum);
  const [start,        setStart]        = useState(eintrag.startISO.slice(11, 16) || '08:00');
  const [ende,         setEnde]         = useState(eintrag.endeISO?.slice(11, 16) ?? '09:00');
  const [mandantId,    setMandantId]    = useState(eintrag.mandantId);
  const [leistung,     setLeistung]     = useState(eintrag.leistungCode);
  const [beschreibung, setBeschreibung] = useState(eintrag.beschreibung);

  const dauerMin = useMemo(() => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = ende.split(':').map(Number);
    return Math.max(0, ((eh ?? 0) * 60 + (em ?? 0)) - ((sh ?? 0) * 60 + (sm ?? 0)));
  }, [start, ende]);

  const handleSave = (): void => {
    if (dauerMin <= 0) return;
    onSave({
      ...eintrag,
      datum,
      startISO: `${datum}T${start}:00`,
      endeISO:  `${datum}T${ende}:00`,
      dauerMin,
      mandantId,
      leistungCode: leistung,
      beschreibung,
    });
  };

  return (
    <div className="px-4 py-3 bg-blue-50/50 dark:bg-blue-950/10 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="space-y-1 col-span-2">
          <label className="label-text">Datum</label>
          <input type="date" value={datum} onChange={e => { setDatum(e.target.value); }} className="input-field text-sm" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Start</label>
          <input type="time" value={start} onChange={e => { setStart(e.target.value); }} className="input-field text-sm" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Ende</label>
          <input type="time" value={ende} onChange={e => { setEnde(e.target.value); }} className="input-field text-sm" />
        </div>
      </div>
      <div className="space-y-1">
        <label className="label-text">Firma</label>
        <select value={mandantId} onChange={e => { setMandantId(e.target.value); }} className="input-field text-sm">
          <option value="">— keine —</option>
          {mandanten.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <label className="label-text">Leistungsart</label>
        <select value={leistung} onChange={e => { setLeistung(e.target.value); }} className="input-field text-sm">
          {LEISTUNGEN.filter(l => l.einheit === 'Std').map(l => (
            <option key={l.code} value={l.code}>{l.code} — {l.bezeichnung}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="label-text">Beschreibung</label>
        <input value={beschreibung} onChange={e => { setBeschreibung(e.target.value); }} className="input-field text-sm" />
      </div>
      {dauerMin <= 0 && <p className="text-xs text-red-500 font-medium">⚠ Ende muss nach Start liegen</p>}
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={dauerMin <= 0}
          className="btn-primary text-sm disabled:opacity-50">✓ Speichern</button>
        <button onClick={onCancel} className="btn-secondary text-sm">Abbrechen</button>
      </div>
    </div>
  );
}

// ── Eintrags-Liste ────────────────────────────────────────────────────────

function ZeitListe({ eintraege, onToggle, onDelete, onUpdate }: {
  eintraege:  ZeitEintragDB[];
  onToggle:   (id: string) => void;
  onDelete:   (id: string) => void;
  onUpdate:   (e: ZeitEintragDB) => void;
}): React.JSX.Element {
  const [editId, setEditId] = useState<string | null>(null);
  const totalMin  = eintraege.reduce((s, e) => s + e.dauerMin, 0);
  const offeneMin = eintraege.filter(e => !e.abgerechnet).reduce((s, e) => s + e.dauerMin, 0);

  if (eintraege.length === 0) {
    return (
      <div className="card text-center py-10 text-gray-400">
        <p className="text-3xl mb-2">⏱</p>
        <p className="font-medium">Noch keine Zeiteinträge</p>
        <p className="text-sm mt-1">Stoppuhr starten oder manuell erfassen</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden p-0">
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <span className="text-gray-500">Gesamt: <span className="font-bold text-gray-900 dark:text-gray-100">{(totalMin / 60).toFixed(2)} h</span></span>
          <span className="text-gray-500">Offen: <span className="font-bold text-orange-600">{(offeneMin / 60).toFixed(2)} h</span></span>
        </div>
        <span className="text-xs text-gray-400">{eintraege.length} Einträge</span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {[...eintraege].reverse().map(e => {
          const l = LEISTUNG_MAP.get(e.leistungCode);
          const h = Math.floor(e.dauerMin / 60);
          const m = e.dauerMin % 60;
          if (editId === e.id) {
            return (
              <EintragEditForm key={e.id} eintrag={e}
                onSave={updated => { onUpdate(updated); setEditId(null); }}
                onCancel={() => { setEditId(null); }} />
            );
          }
          return (
            <div key={e.id} className="flex items-start gap-3 px-4 py-3">
              <input type="checkbox" checked={e.abgerechnet} onChange={() => { onToggle(e.id); }}
                className="mt-1 rounded border-gray-300 text-green-600 flex-shrink-0"
                title="Als abgerechnet markieren" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {l?.bezeichnung ?? e.leistungCode}
                  {e.abgerechnet && (
                    <span className="ml-2 text-xs bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400 px-1.5 py-0.5 rounded-full">abgerechnet</span>
                  )}
                </p>
                {e.beschreibung && <p className="text-xs text-gray-500 truncate">{e.beschreibung}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  {e.datum} · {e.startISO.slice(11,16)}–{e.endeISO?.slice(11,16) ?? '…'} · {e.mandantId}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold font-mono text-gray-900 dark:text-gray-100">
                  {h > 0 ? `${h}h ` : ''}{m > 0 ? `${m}m` : ''}
                </p>
                <div className="flex items-center gap-1.5 justify-end mt-0.5">
                  <button onClick={() => { setEditId(e.id); }}
                    className="text-base text-gray-400 dark:text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded px-1.5 py-0.5 transition-colors"
                    title="Bearbeiten">
                    ✎
                  </button>
                  <button onClick={() => { onDelete(e.id); }}
                    className="text-base text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded px-1.5 py-0.5 transition-colors"
                    title="Löschen">
                    🗑
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function ZeitModul(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [eintraege,      setEintraege]      = useState<ZeitEintragDB[]>([]);
  const [activeLeistung, setActiveLeistung] = useState('BER-01');
  const [loading,        setLoading]        = useState(true);

  // Laden aus DB
  useEffect(() => {
    void loadZeiteintraege(mandant?.id).then(data => {
      setEintraege(data);
      setLoading(false);
    });
  }, [mandant?.id]);

  const handleStoppuhrStop = (dauerMin: number, startISO: string, endeISO: string): void => {
    const neu: ZeitEintragDB = {
      id:          crypto.randomUUID(),
      datum:       startISO.slice(0, 10),
      startISO,
      endeISO,
      dauerMin,
      mandantId:   mandant?.id ?? '',
      leistungCode: activeLeistung,
      beschreibung: '',
      abgerechnet:  false,
      erstelltAm:   new Date().toISOString(),
    };
    void addZeitEintrag(neu).then(() => {
      setEintraege(prev => [...prev, neu]);
    });
  };

  const handleManuelAdd = (e: Omit<ZeitEintragDB, 'id' | 'erstelltAm'>): void => {
    const neu: ZeitEintragDB = { ...e, id: crypto.randomUUID(), erstelltAm: new Date().toISOString() };
    void addZeitEintrag(neu).then(() => {
      setEintraege(prev => [...prev, neu]);
    });
  };

  const handleToggle = (id: string): void => {
    void toggleAbgerechnet(id).then(() => {
      setEintraege(prev => prev.map(e => e.id === id ? { ...e, abgerechnet: !e.abgerechnet } : e));
    });
  };

  const handleDelete = (id: string): void => {
    void deleteZeitEintrag(id).then(() => {
      setEintraege(prev => prev.filter(e => e.id !== id));
    });
  };

  const handleUpdate = (updated: ZeitEintragDB): void => {
    void updateZeitEintrag(updated).then(() => {
      setEintraege(prev => prev.map(e => e.id === updated.id ? updated : e));
    });
  };

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Zeit & Leistung</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Zeiterfassung für
          {mandant !== null && mandant !== undefined
            ? <span className="ml-1 font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span>
            : ' alle Mandanten'
          }
        </p>
      </div>

      <div className="flex items-center gap-3">
        <label className="label-text flex-shrink-0">Leistungsart (Stoppuhr):</label>
        <select value={activeLeistung} onChange={e => { setActiveLeistung(e.target.value); }}
          className="input-field max-w-xs text-sm">
          {LEISTUNGEN.filter(l => l.einheit === 'Std').map(l => (
            <option key={l.code} value={l.code}>{l.code} — {l.bezeichnung}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Lade Einträge…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="space-y-5">
              <Stoppuhr onStop={handleStoppuhrStop} />
              <ManuelleEingabe onAdd={handleManuelAdd} />
            </div>
            <div className="lg:col-span-2 space-y-5">
              <Wochenuebersicht eintraege={eintraege} />
              <ZeitListe eintraege={eintraege} onToggle={handleToggle} onDelete={handleDelete} onUpdate={handleUpdate} />
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-gray-400 text-center">
        ✅ Persistenz in verschlüsselter Dexie-DB · ✎ Bearbeiten und 🗑 Löschen pro Eintrag
      </p>
    </div>
  );
}
